import { getBezierPath } from "@xyflow/react";

import type { RuleEdgeProps } from "../types/edges";
import { EDGE_STROKE, EdgeAddButtonLayer } from "./BaseEdge";
import { forEachSelfLoopPathD, getForEachLoopStartEntryPlacement } from "./bezierEdgeUtils";

const PILL_WIDTH = 60;
const PILL_HEIGHT = 24;
const HALF_PILL_WIDTH = PILL_WIDTH / 2;
const HALF_PILL_HEIGHT = PILL_HEIGHT / 2;

const forEachEdgeData = (data: RuleEdgeProps["data"]) => ({
	...data,
	label: "For Each",
	color: "#01D5AB",
});

const LoopForEachEdge = (props: RuleEdgeProps) => {
	const mergedData = forEachEdgeData(props.data);

	let pathD: string;
	let labelX: number;
	let labelY: number;
	let addX: number;
	let addY: number;

	if (props.source === props.target) {
		const self = forEachSelfLoopPathD(
			props.sourceX,
			props.sourceY,
			props.targetX,
			props.targetY,
		);
		pathD = self.d;
		labelX = self.labelX;
		labelY = self.labelY;
		addX = self.addX;
		addY = self.addY;
	} else {
		[pathD] = getBezierPath({
			sourceX: props.sourceX,
			sourceY: props.sourceY,
			sourcePosition: props.sourcePosition,
			targetX: props.targetX,
			targetY: props.targetY,
			targetPosition: props.targetPosition,
		});
		const placement = getForEachLoopStartEntryPlacement({
			sourceX: props.sourceX,
			sourceY: props.sourceY,
			targetX: props.targetX,
			targetY: props.targetY,
			sourcePosition: props.sourcePosition,
			targetPosition: props.targetPosition,
		});
		labelX = placement.labelX;
		labelY = placement.labelY;
		addX = placement.addX;
		addY = placement.addY;
	}

	const pillColor = mergedData?.color ?? "#01D5AB";

	return (
		<>
			<path
				id={props.id}
				d={pathD}
				style={{
					stroke: EDGE_STROKE,
					strokeWidth: 2.5,
					fill: "none",
					...(props.style ?? {}),
				}}
				markerEnd={props.markerEnd}
			/>

			{mergedData?.label && (
				<g
					transform={`translate(${labelX}, ${labelY})`}
					style={{ pointerEvents: "none", userSelect: "none" }}
				>
					<rect
						x={-HALF_PILL_WIDTH}
						y={-HALF_PILL_HEIGHT}
						width={PILL_WIDTH}
						height={PILL_HEIGHT}
						rx={6}
						ry={6}
						fill="#fff"
					/>
					<rect
						x={-HALF_PILL_WIDTH}
						y={-HALF_PILL_HEIGHT}
						width={PILL_WIDTH}
						height={PILL_HEIGHT}
						rx={6}
						ry={6}
						fill={pillColor}
						fillOpacity={0.12}
						stroke={pillColor}
						strokeWidth={1.5}
					/>
					<text
						textAnchor="middle"
						dominantBaseline="middle"
						style={{
							fontSize: "0.75rem",
							fontWeight: 400,
							fill: pillColor,
							fontFamily: "Roboto, sans-serif",
						}}
					>
						{mergedData.label}
					</text>
				</g>
			)}

			{!mergedData?.disableAddButton && (
				<EdgeAddButtonLayer
					id={props.id}
					source={props.source}
					target={props.target}
					sourceHandleId={props.sourceHandleId}
					sourceX={props.sourceX}
					sourceY={props.sourceY}
					targetX={props.targetX}
					targetY={props.targetY}
					data={mergedData}
					addButtonScreenPosition={{ x: addX, y: addY }}
				/>
			)}
		</>
	);
};

export default LoopForEachEdge;
